import { Link } from "react-router-dom";
import { ArrowRight, FileText, Home as HomeIcon, Scale, MessagesSquare } from "lucide-react";
import { Layout } from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { SectionHead } from "../components/SectionHead";
import { CtaSection } from "../components/CtaSection";
import { CinematicBreak } from "../components/CinematicBreak";
import { usePageMeta } from "../hooks/usePageMeta";
import { CINEMATIC } from "../data/site";
import { useContent } from "../context/ContentContext";
import { fileUrl } from "../lib/files";

const AREAS = [
  {
    icon: FileText,
    num: "01",
    title: "Understanding Your Position",
    text: "We review your tenancy or leasehold situation and explain, in plain language, where you stand and what your options are.",
  },
  {
    icon: HomeIcon,
    num: "02",
    title: "Tenancy Matters",
    text: "Practical guidance on tenancy questions — from agreements and deposits to communicating effectively with landlords or agents.",
  },
  {
    icon: Scale,
    num: "03",
    title: "Leasehold Matters",
    text: "Support in navigating leasehold issues such as service charges and lease terms, with solicitors introduced where legal input is needed.",
  },
  {
    icon: MessagesSquare,
    num: "04",
    title: "Informed Next Steps",
    text: "Every conversation ends with clarity: what you can do, who can help, and how to move forward with confidence.",
  },
];

const STEPS = [
  { num: "01", title: "Make an Enquiry", text: "Tell us briefly about your situation through the contact form or by phone." },
  { num: "02", title: "Initial Consultation", text: "We discuss your circumstances, answer your immediate questions and outline your options." },
  { num: "03", title: "Clear Guidance", text: "You receive practical, independent advice on your position and the next steps available to you." },
  { num: "04", title: "Specialist Support", text: "Where needed, we introduce trusted solicitors or other professionals from our network." },
];

const TenantAdvice = () => {
  usePageMeta(
    "Independent Tenant & Leasehold Advice | Elin's Consultancy",
    "Independent, practical advice for tenants and leaseholders — understand your rights, your position and your next steps."
  );
  const content = useContent();
  const banner = content?.banners?.tenant ? fileUrl(content.banners.tenant) : CINEMATIC.streetArch;
  return (
    <Layout>
      <PageHero
        eyebrow="For Tenants & Leaseholders"
        lines={[
          "Independent",
          <>
            Tenant &amp; <em className="italic font-normal text-gold-soft">Leasehold</em> Advice
          </>,
        ]}
        text="Practical, independent advice to help you understand your rights, navigate tenancy or leasehold issues and make informed decisions."
        image={banner}
        imageAlt="A grand stone archway on a residential London street in warm morning light"
      />

      <section data-testid="advice-areas" className="bg-cream">
        <SectionHead
          eyebrow="How We Help"
          title="Clear Advice, On Your Side"
          text="Our guidance is completely independent — never tied to a landlord, agent or developer."
        />
        <div className="wrap-x pb-20 sm:pb-24 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {AREAS.map((a, i) => (
            <Reveal key={a.num} delay={i * 0.08}>
              <article
                data-testid={`advice-area-${a.num}`}
                className="bg-cream-3 border border-forest/10 p-8 sm:p-10 h-full transition-colors duration-500 hover:border-gold/60"
              >
                <div className="flex items-center justify-between">
                  <a.icon size={30} strokeWidth={1.3} className="text-gold" />
                  <span className="font-serif italic text-xl text-gold-dark">{a.num}</span>
                </div>
                <div className="w-full h-px bg-forest/15 my-6" />
                <h3 className="font-serif text-2xl font-semibold text-forest">{a.title}</h3>
                <p className="text-[13.5px] leading-[1.7] text-sage mt-3">{a.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <CinematicBreak
        image={CINEMATIC.livingWarmWide}
        alt="A warm, welcoming living room interior in natural light"
        caption="On Your Side"
        quote="You shouldn't need a law degree to understand your own home. We make it clear."
      />

      <section data-testid="advice-steps" className="bg-forest text-cream relative grain">
        <SectionHead
          dark
          eyebrow="Making an Enquiry"
          title="From Question to Clarity"
          text="A straightforward route to the advice you need."
        />
        <div className="wrap-x relative z-[2] pb-20 sm:pb-24 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-y-12">
          {STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.1} className="relative px-5 text-center">
              {i < STEPS.length - 1 && (
                <span aria-hidden="true" className="hidden xl:block absolute top-[30px] -right-3 w-6 h-px hairline-gold" />
              )}
              <div className="w-[60px] h-[60px] rounded-full border border-gold mx-auto flex items-center justify-center font-serif italic text-xl text-gold-soft">
                {s.num}
              </div>
              <h3 className="font-serif text-xl font-semibold mt-5 mb-2.5">{s.title}</h3>
              <p className="text-xs leading-[1.65] text-cream/55">{s.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-cream">
        <div className="wrap-x py-16 text-center">
          <Reveal>
            <p className="text-sm text-sage max-w-lg mx-auto leading-relaxed">
              Every situation is different. Tell us yours in confidence, and we&rsquo;ll explain
              where you stand.
            </p>
            <Link to="/contact" data-testid="advice-enquire-button" className="btn-forest mt-8">
              Request Advice <ArrowRight size={14} strokeWidth={2.2} />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaSection
        title={
          <>
            Get Clarity on Your
            <br />
            <em className="italic font-normal text-gold-soft">Property Matter</em>
          </>
        }
        text="Whether it's a tenancy question or a leasehold concern, an initial conversation can give you a clear way forward."
      />
    </Layout>
  );
};

export default TenantAdvice;
